/**
 * The Lab (decision 123; BUILDLIST 67.5): one dev-only panel for every experimental switch, so the owner
 * can play with the new styles and configs without the menu growing a button per experiment. Rows are the
 * fields of a `LabConfig` (labConfig.ts), saved per browser; **Launch** starts the match through the
 * existing real-time launch path, **Copy as URL** puts the equivalent `?action&…` link on the clipboard.
 * The Lab may be ugly; the menu stays clean.
 */

import { LAB_DEFAULTS, LAB_STORAGE_KEY, dropLegacy, labForceText, labLaunch, labUrl, normalizeLabConfig, type LabConfig, type LabLaunch } from './labConfig';
import { LAB_ROWS, LAB_SYSTEMS, LEGACY_PARAM, labDialIdle, labMasterOff, sideOf, sided, type LabRow, type LabLaterRow } from './labRows';
import { LAB_PRESETS, presetNames, presetConfig, presetOf, presetSlug, savePreset, deletePreset, isShippedPreset } from './labPresets';
import { setFineGroundDrawn } from '../rendering/fineTerrainPatch';
import { setFarLiftShare } from '../rendering/groundLift';
import { setFarWoodsDrawn } from '../rendering/farCanopy';
import { setTiltShiftEnabled } from '../rendering/tiltShift';
import { setAimMode } from '../game/aimMode';
import { setAimAssist } from '../game/aimTarget';
import { setCommandLayout } from './commandLayout';
import { setCorpseConfig } from '../game/corpseStore';
import { setLawSpentConfig } from '../game/lawSpent';
import { setVehicleVolume } from '../audio/vehicleSound';
import { setLitterScale } from '../rendering/groundLitter';
import { setTreeDrawMode } from '../rendering/treeDrawMode';
import { setForestDrawn } from '../rendering/forestDraw';
import { setTopoEnabled, setTopoDensity } from '../rendering/topoOverlay';
import { setPlanetTable } from '../game/planetTableView';
import { setRadarCoverage } from './radarCoverage';
import { setHitShapesShown } from '../rendering/hitShapesFlag';
import { setRearmArmShown } from '../rendering/rearmArm';
import { setBrassShown } from '../rendering/brass';
import { setMotorPoolShown } from '../rendering/motorPoolPlay';
import { closeLoadoutBuilder, openLoadoutDrawer } from './loadoutBuilder';
import { parseLayout, layoutSquads } from '../core/loadout';
import { LAYOUT_MAX_PEOPLE } from '../constants/loadout';

function load(): LabConfig {
  try { return retired(normalizeLabConfig(JSON.parse(localStorage.getItem(LAB_STORAGE_KEY) ?? 'null'))); } catch { return { ...LAB_DEFAULTS }; }
}
/** 69.13 (d): without legacy=1 the retired rows (the gun pit's) at their defaults — a stored config or a preset that had them does not bring them back unseen. */
function retired(c: LabConfig): LabConfig { return legacyOn() ? c : dropLegacy(c); }
function save(c: LabConfig): void {
  try { localStorage.setItem(LAB_STORAGE_KEY, JSON.stringify(c)); } catch { /* storage unavailable */ }
}

let config: LabConfig = load();
export function labConfig(): LabConfig { return config; }

type Row = LabRow | LabLaterRow;
/** Every experimental switch, by system, the master first (labRows.ts: insert a new row in its system, never at the end). */
/** 69.13 (d): the retired rows (the gun pit's) shown only beside legacy=1 in the page's URL. */
function legacyOn(): boolean { try { return new URLSearchParams(location.search).get(LEGACY_PARAM) === '1'; } catch { return false; } }   // (a declaration: load() runs before this line)
const ROWS: ReadonlyArray<Row> = LAB_ROWS.filter(r => !(r as { legacy?: true }).legacy || legacyOn());

function esc(s: string): string { return s.replace(/[&<>"]/g, ch => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' }[ch] as string)); }

// 62.58 step 1: a system's title over its rows; a dial (a row `under` its master) drawn indented beneath it, and greyed —
// its control disabled — while the master is off (the rule it feeds does nothing then). The restyle (index.html's .lab-*):
// the title a header with its master's state as a chip; a dial on a guide line, saying what it waits for while idle;
// captions over a per-team row's two fields and a counts row's parts.
function rowOf(key: keyof LabConfig): Row | undefined { return ROWS.find(r => r.kind !== 'later' && r.key === key); }

/** A master's value as the chip says it: on/off, the select's word, a pair as "yours / enemy", a counts row's parts. */
function stateText(v: unknown): string {
  if (Array.isArray(v)) return `${stateText(v[0])} / ${stateText(v[1])}`;
  if (typeof v === 'boolean') return v ? 'on' : 'off';
  if (v && typeof v === 'object') return Object.entries(v as Record<string, number>).map(([k, n]) => `${k} ${n}`).join(' · ');
  return String(v);
}

function systemHtml(system: (typeof LAB_SYSTEMS)[number], c: LabConfig): string {
  const m = system.master !== undefined ? c[system.master] as unknown : undefined;
  const off = m !== undefined && (Array.isArray(m) ? m.every(labMasterOff) : labMasterOff(m));
  const chip = m !== undefined ? `<span class="lab-chip${off ? ' off' : ''}" title="${esc(rowOf(system.master!)?.label ?? '')}">${esc(stateText(m))}</span>` : '';
  return `<div class="lab-system"><span class="lab-system-title">${esc(system.title)}</span>${chip}</div>`;
}

/** What an idle dial waits for: its master on, or at the one value it reads (the weather's cells). */
function waitText(r: LabRow): string {
  const master = r.under !== undefined ? rowOf(r.under)?.label ?? String(r.under) : '';
  return r.underIs !== undefined ? `with ${master}: ${r.underIs}` : `with ${master} on`;
}

/** 65.32: a side's layout total beside its row — the side's people, and when the 50 cap clamped it. */
function layoutTotal(v: unknown): string {
  const l = parseLayout(String(v));
  if (!l) return '';
  const r = layoutSquads(l);
  return `<span class="lab-wait">= ${r.people}${r.clamped ? ` (clamped to whole squads within ${LAYOUT_MAX_PEOPLE})` : ''}</span>`;
}

/** 69.49: what each side fields with the Doctrine row on (the composed counts), beside the row. */
function forceTotal(c: LabConfig): string {
  const t = labForceText(c);
  return t ? `<span class="lab-wait">= ${esc(t)}</span>` : '';
}

function rowHtml(r: Row, c: LabConfig, masters: ReadonlySet<keyof LabConfig>): string {
  const dial = r.kind !== 'later' && r.under !== undefined, idle = r.kind !== 'later' && labDialIdle(r, c);
  const off = idle ? ' disabled' : '';
  const wait = idle ? `<span class="lab-wait">${esc(waitText(r))}</span>` : '';
  const note = r.note ? `<div class="lab-note">${esc(r.note)}</div>` : '';
  const label = `<div class="lab-text"><span class="setting-label">${esc(r.label)}</span>${wait}${note}</div>`;
  // (62.58 step 2: a per-team row draws a control a side — yours, then the enemy's; `data-side` says which half it sets)
  const cap = (t: string, field: string, cls = '') => `<label class="lab-cap${cls ? ' ' + cls : ''}"><span>${t}</span>${field}</label>`;
  const one = (v: unknown, side?: 0 | 1, w = 150): string => {
    if (r.kind === 'later') return `<span class="setting-toggle off" style="cursor:default;">LATER</span>`;
    const at = `data-key="${r.key}"${side !== undefined ? ` data-side="${side}"` : ''}`;
    if (r.kind === 'select') return `<select class="menu-input lab-field" ${at} style="width:${w}px;">${r.options.map(o => `<option value="${o}"${String(v) === o ? ' selected' : ''}>${o}</option>`).join('')}</select>`;
    if (r.kind === 'number') return `<input class="menu-input lab-field" ${at} type="number" min="${r.min}" max="${r.max}" value="${v}" style="width:90px;">`;
    if (r.kind === 'combo') return `<input class="menu-input lab-field" ${at} type="text" list="lab-list-${r.key}" value="${esc(String(v))}" style="width:${w}px;"><datalist id="lab-list-${r.key}">${r.options.map(o => `<option value="${o}"></option>`).join('')}</datalist>` + (r.key === 'layout' ? layoutTotal(v) : '');
    if (r.kind === 'text') return `<input class="menu-input lab-field" ${at} type="text" value="${esc(String(v))}" style="width:${w}px;">`;
    // (62.58 step 3: a counts row, a number a part — `data-part` says which)
    if (r.kind === 'counts') return r.parts.map(x => cap(esc(x.label), `<input class="menu-input lab-field" ${at} data-part="${x.part}" type="number" min="${r.min}" max="${x.max ?? r.max}" value="${(v as Record<string, number>)[x.part]}" style="width:64px;">`)).join('');
    return `<button class="setting-toggle lab-field${v ? '' : ' off'}" ${at} data-kind="toggle">${v ? 'ON' : 'OFF'}</button>`;
  };
  const split = r.kind !== 'later' && r.perTeam && Array.isArray(c[r.key]);   // the two sides differ
  const inner = r.kind !== 'later' && r.perTeam
    ? cap('yours', one(sideOf(c[r.key] as unknown, 0), 0, 110), 'yours') + cap('enemy', one(sideOf(c[r.key] as unknown, 1), 1, 110), 'enemy')
    : one(r.kind === 'later' ? null : c[r.key]);
  // 65.34: the loadout rows open the builder (the drawer on their tab)
  const builder = r.kind !== 'later' && (r.key === 'layout' || r.key === 'loadout' || r.key === 'squadTypes')
    ? `<button class="lab-builder" data-builder="${r.key === 'layout' ? 'sides' : r.key === 'loadout' ? 'you' : 'types'}" style="margin-left:6px;">Loadouts ▸</button>` : '';
  const control = `<div class="lab-control${split ? ' split' : ''}">${inner}${builder}${r.kind !== 'later' && r.key === 'forceDoctrine' ? forceTotal(c) : ''}</div>`;
  const cls = ['setting-row', 'lab-row', r.kind === 'later' ? 'later' : '', dial ? 'dial' : '', idle ? 'idle' : '', r.kind !== 'later' && masters.has(r.key) ? 'master' : ''].filter(Boolean).join(' ');
  return `<div class="${cls}"${idle ? ` title="off ${esc(waitText(r))}"` : ''}>${label}${idle ? control.replace(/<(select|input|button)/g, `<$1${off}`) : control}</div>`;
}

/**
 * The presets bar: pick one to load it (the Lab's defaults plus its rows; its seed pins the world), save the Lab as it
 * stands under a name (this browser), delete one of yours. The shipped ones cannot be overwritten or deleted.
 */
function renderPresets(): void {
  const bar = document.getElementById('lab-presets');
  if (!bar) return;
  const current = presetOf(config);
  const shipped = current ? LAB_PRESETS.find(p => p.name === current) : undefined;
  const opts = [`<option value=""${current ? '' : ' selected'}>— preset —</option>`, ...presetNames().map(n => `<option value="${esc(n)}"${n === current ? ' selected' : ''}>${esc(n)}${isShippedPreset(n) ? '' : ' (yours)'}</option>`)];
  const note = current ? `${shipped ? esc(shipped.note) + ' · ' : ''}<span style="font-family:'JetBrains Mono',monospace;">?preset=${esc(presetSlug(current))}</span>` : 'Pick a preset to load it, or save this Lab under a name.';
  bar.innerHTML = `<div class="setting-row" style="gap:8px; flex-wrap:wrap;">
      <select class="menu-input" id="lab-preset" style="flex:1 1 160px;">${opts.join('')}</select>
      <input class="menu-input" id="lab-preset-name" type="text" placeholder="name" maxlength="40" style="flex:1 1 110px;" value="${current && !isShippedPreset(current) ? esc(current) : ''}">
      <button class="menu-btn" id="btn-lab-preset-save" style="flex:0 0 auto;">Save</button>
      <button class="menu-btn" id="btn-lab-preset-delete" style="flex:0 0 auto;"${current && !isShippedPreset(current) ? '' : ' disabled'}>Delete</button>
    </div>
    <div id="lab-preset-note" style="font-family:'Space Grotesk',sans-serif; font-size:10px; opacity:0.55; line-height:1.5; padding:2px 0 0;">${note}</div>`;
  document.getElementById('lab-preset')?.addEventListener('change', e => {
    const c = presetConfig((e.target as HTMLSelectElement).value);
    if (c) update({ ...c });
  });
  document.getElementById('btn-lab-preset-save')?.addEventListener('click', () => {
    const name = (document.getElementById('lab-preset-name') as HTMLInputElement | null)?.value ?? '';
    if (savePreset(name, config)) { renderLab(); return; }
    const n = document.getElementById('lab-preset-note');
    if (n) n.textContent = isShippedPreset(name) ? `"${name}" is a shipped preset: pick another name.` : 'Give the preset a name first.';
  });
  document.getElementById('btn-lab-preset-delete')?.addEventListener('click', () => {
    if (current && !isShippedPreset(current)) { deletePreset(current); renderLab(); }
  });
}

/** Draw the rows into the panel from the current config. */
export function renderLab(): void {
  renderPresets();
  const rows = document.getElementById('lab-rows');
  if (!rows) return;
  let system: string | null = null;
  const masters = new Set(LAB_SYSTEMS.flatMap(x => x.master !== undefined ? [x.master] : []));
  rows.innerHTML = ROWS.map(r => {
    const sys = LAB_SYSTEMS.find(x => x.system === r.system);
    const head = r.system !== system ? (sys ? systemHtml(sys, config) : `<div class="lab-system"><span class="lab-system-title">${esc(r.system)}</span></div>`) : '';
    system = r.system;
    return head + rowHtml(r, config, masters);
  }).join('');
  const url = document.getElementById('lab-url');
  if (url) url.textContent = labUrl(config);
  for (const b of Array.from(rows.querySelectorAll<HTMLElement>('button.lab-builder'))) {
    b.addEventListener('click', ev => { ev.preventDefault(); openLoadoutDrawer({ config: () => config, update }, b.dataset.builder as 'types' | 'sides' | 'you'); });
  }
  for (const el of Array.from(rows.querySelectorAll<HTMLElement>('.lab-field'))) {
    const key = el.dataset.key as keyof LabConfig;
    // (a per-team control sets its side of the pair; the parser makes one value of a pair that agrees)
    const side = el.dataset.side === '0' ? 0 : el.dataset.side === '1' ? 1 : null;
    const part = el.dataset.part;
    const put = (v: unknown) => part !== undefined ? { ...(config[key] as unknown as Record<string, number>), [part]: v }
      : side === null ? v : side === 0 ? sided(v, sideOf(config[key] as unknown, 1)) : sided(sideOf(config[key] as unknown, 0), v);
    if (el.dataset.kind === 'toggle') {
      el.addEventListener('click', () => { update({ [key]: put(!(side === null ? config[key] : sideOf(config[key] as unknown, side))) }); });
    } else {
      el.addEventListener('change', () => { update({ [key]: put((el as HTMLInputElement).value) }); });
    }
  }
}

function update(patch: Partial<Record<keyof LabConfig, unknown>>): void {
  config = retired(normalizeLabConfig({ ...config, ...patch }));   // (a preset loaded here too)
  save(config);
  renderLab();
}

/**
 * Wire the panel: `launch` is the game's real-time launch (Game.startActionFromSetup), so the Lab has no
 * second game path to keep in sync. The fine-ground flag is set before launching, as the URL's `fineground=1` would.
 */
export function setupLab(launch: (l: LabLaunch) => void): void {
  renderLab();
  document.getElementById('btn-lab-launch')?.addEventListener('click', () => {
    setFineGroundDrawn(config.fineGround ? true : null);
    setFarLiftShare(config.farLift === '0.5' ? null : Number(config.farLift));   // 67.50 (3); 71.3 (a): the relief's mean the default (the URL's ?farlift= then), 0 off
    setFarWoodsDrawn(config.farWoods);   // 67.46 (5)
    setTiltShiftEnabled(config.tiltShift ? null : false);   // 68.14: on (the owner's choice) unless the row (or ?tiltshift=0) turns it off   // 68.12: the commander's overview on O
    setAimMode(config.aim);
    setAimAssist(Number(config.aimAssist));   // 65.25a
    setBrassShown(config.brass ? null : false);   // 65.47: on (the owner: "Brass by default.") unless the row (or ?brass=0) turns it off
    setCommandLayout(config.commandLayout);   // 68.41
    setCorpseConfig({ seconds: config.corpseTime, cap: config.corpseCap, fade: config.corpseFade });   // X33 1b
    setLawSpentConfig({ seconds: config.tubeTime, most: config.tubeCap });   // S7: the spent tubes' dials
    setVehicleVolume(Number(config.vehicleVolume));   // 67.55
    setLitterScale(Number(config.litter));   // 67.61
    setMotorPoolShown(config.motorPool ? true : null);   // 67.75 (off: ?motorpool=1 still draws it)
    setRearmArmShown(config.rearmArm ? true : null);   // 69.31 (off: ?rearmarm=1 still draws it)
    setTreeDrawMode(config.trees);
    setForestDrawn(config.forest ? true : false);   // E5.4 step 2 (on by default; off, not drawn either)
    setHitShapesShown(config.hitShapes ? true : null);
    setTopoEnabled(config.topoOverlay);   // 68.17
    setPlanetTable(config.planetTable);   // 68.42
    setRadarCoverage(config.radarCoverage ? true : null);   // 68.43 (off: ?radarcover=1 still draws it)
    setTopoDensity(config.topoDensity === 'auto' ? 'auto' : (Number(config.topoDensity) as 1 | 5 | 25));   // 68.17's follow-up
    closeLoadoutBuilder();   // 65.34: the drawer is the Lab's; it never rides into the game
    launch(labLaunch(config));
  });
  document.getElementById('btn-lab-back')?.addEventListener('click', () => closeLoadoutBuilder());   // (nor stays over the menu)
  document.getElementById('btn-lab-copy')?.addEventListener('click', async () => {
    const text = labUrl(config, `${location.origin}${location.pathname}`);
    const btn = document.getElementById('btn-lab-copy');
    try { await navigator.clipboard.writeText(text); if (btn) btn.textContent = 'COPIED'; }
    catch { if (btn) btn.textContent = 'SELECT BELOW'; }
    setTimeout(() => { if (btn) btn.textContent = 'COPY AS URL'; }, 1500);
  });
  document.getElementById('btn-lab-reset')?.addEventListener('click', () => { update({ ...LAB_DEFAULTS }); });
}
